import { getLocalePathPrefixFromUrl } from '../lib/urlCountryCode'

import type { Environment, EnvironmentWithChina } from '../App'

export const API_BASE_URLS: Record<Environment, string> = {
  tst: 'https://tst.apim.suitsupply.com',
  acc: 'https://acc.apim.suitsupply.com',
  prd: 'https://apim.suitsupply.com',
} as const

export const CUSTOMER_TIMELINE_BASE_URLS: Record<Environment, string> = {
  tst: 'https://ext-customer-profile-web-tst.azurewebsites.net',
  acc: 'https://ext-customer-profile-web-tst.azurewebsites.net',
  prd: 'https://ext-customer-profile-web-prd.azurewebsites.net',
} as const

export const CUSTOMER_SEARCH_APP_URLS: Record<Environment, string> = {
  tst: 'https://monorepo-customer-search-testing.vercel.app',
  acc: 'https://monorepo-customer-search-acceptance.vercel.app',
  prd: 'https://monorepo-rp-customer-search.vercel.app',
} as const

export const CUSTOMER_PROFILE_STANDALONE_APP_URLS: Record<Environment, string> =
  {
    tst: 'https://monorepo-rp-customer-profile-testing.vercel.app',
    // Acceptance currently reuses the testing standalone app.
    acc: 'https://monorepo-rp-customer-profile-testing.vercel.app',
    prd: 'https://monorepo-customer-profile.vercel.app',
  } as const

export const WEBSITE_BASE_URLS: Record<Environment, string> = {
  tst: 'https://testing.suitsupply.com',
  acc: 'https://acceptance.suitsupply.com',
  prd: 'https://suitsupply.com',
} as const

const CHINA_API_BASE_URLS: Record<'cn_tst' | 'cn_prd', string> = {
  cn_tst: 'https://testing.apim.cn1.suitsupply.com',
  cn_prd: 'https://apim.cn1.suitsupply.com',
}

export function getApiBaseUrl(env: EnvironmentWithChina): string {
  if (env === 'cn_tst' || env === 'cn_prd') {
    return CHINA_API_BASE_URLS[env]
  }

  return API_BASE_URLS[env]
}

export function getWebsiteBaseUrl(env: Environment): string {
  return WEBSITE_BASE_URLS[env]
}

/**
 * Locale for a storefront link, resolved the way `getReturnsUrl` resolves it:
 * the host page's own path prefix first — it is the locale the customer is
 * actually browsing, language included — then `en-<country>` for the in-store
 * hosts whose path carries no locale.
 *
 * A link built without one is matched by the storefront's optional `($locale)`
 * route and falls through to its default locale, which sent GB customers to
 * `en-us`.
 */
function resolveStorefrontLocale(
  countryCode?: string | null,
  localePrefix = getLocalePathPrefixFromUrl(),
): string {
  return localePrefix ?? `en-${(countryCode || 'nl').toLowerCase()}`
}

export function getNewArrivalsUrl(
  env: Environment,
  countryCode?: string | null,
  localePrefix = getLocalePathPrefixFromUrl(),
): string {
  const locale = resolveStorefrontLocale(countryCode, localePrefix)

  return `${getWebsiteBaseUrl(env)}/${locale}/men/new-arrivals`
}

export function getStoresUrl(
  env: Environment,
  countryCode?: string | null,
  localePrefix = getLocalePathPrefixFromUrl(),
): string {
  const locale = resolveStorefrontLocale(countryCode, localePrefix)

  return `${getWebsiteBaseUrl(env)}/${locale}/stores`
}

/**
 * Appointment tool (Waitwhile). Single host for every environment — unlike the
 * booking tool's own config there is no `tst`/`acc` deployment of this flow, so
 * a hub on testing still books against the live appointment tool.
 */
const APPOINTMENT_BASE_URL = 'https://appointment.suitsupply.com/v2/'

/** Waitwhile service that represents collecting an order in store. */
const PICKUP_SERVICE_ID = 'M7eqRWLNEAIfFAxr0jmz'

/**
 * The appointment tool opens straight into employee identification — the hub's
 * pick-up flow is started by a style advisor on the shop floor, not by the
 * customer picking a store.
 */
const PICKUP_SCREEN = 'employee-id'

export interface PickupAppointmentParams {
  /** `store_code` from the trait, e.g. `SR-IT-Milan-CorsoMonforte`. */
  storeCode: string
  /** `store_country_code` from the trait — which store the booking is at. */
  storeCountryCode: string | null | undefined
  /** Host app UI language, e.g. `en`. */
  language: string
  /**
   * Host app country code. Drives `locale` only; the store's own country drives
   * `location`, so a Dutch-speaking advisor booking in Milan keeps Dutch UI.
   */
  countryCode: string
}

/**
 * Deep link into the appointment tool for collecting an order at a store.
 *
 * `location` is the *store's* country while `locale` carries the *host app's* —
 * they usually match, but they are deliberately separate inputs.
 */
export function getPickupAppointmentUrl({
  storeCode,
  storeCountryCode,
  language,
  countryCode,
}: PickupAppointmentParams): string {
  const lang = language.split('-')[0].toLowerCase()
  const appCountry = countryCode.toLowerCase()
  const storeCountry = (storeCountryCode || appCountry).toLowerCase()

  const params = new URLSearchParams({
    language: lang,
    locale: `${lang}-${appCountry}`,
    location: storeCountry,
    screen: PICKUP_SCREEN,
    'location-code': storeCode,
    'service-id': PICKUP_SERVICE_ID,
  })

  return `${APPOINTMENT_BASE_URL}?${params.toString()}`
}

/**
 * The returns page for one order. Always locale-prefixed.
 *
 * A locale-less link is redirected by the storefront's `($locale)` route to
 * `en-us`, and the returns page reads its country straight off that locale — so
 * a GB customer was being sent to the US returns provider and its options.
 *
 * The host page's own path prefix wins, because it is the locale the customer is
 * actually browsing, language included (`de-at`, not `en-at`). Hosts whose path
 * carries no locale — the in-store apps, and the standalone dev server — fall
 * back to the resolved country, which `getResolvedCountryCode` takes from
 * `?countryCode`, then the path, then the customer's own record. Language is
 * `en` there because that is all we can infer; every country in the
 * storefront's `COUNTRIES_CONFIG` has an `en-xx` locale, so the prefix is always
 * one the `($locale)` route accepts. Same shape as
 * `getSizePassportJournalUrl`.
 *
 * Both sources injectable for tests, matching `getResolvedCountryCode`.
 */
export function getReturnsUrl(
  env: Environment,
  orderId: string,
  countryCode?: string | null,
  localePrefix = getLocalePathPrefixFromUrl(),
): string {
  const locale = resolveStorefrontLocale(countryCode, localePrefix)

  return `${getWebsiteBaseUrl(env)}/${locale}/returns?orderId=${encodeURIComponent(orderId)}`
}

export function getSizePassportJournalUrl(
  env: Environment,
  countryCode: string | null | undefined,
): string {
  const country = (countryCode || 'nl').toLowerCase()
  return `${getWebsiteBaseUrl(env)}/en-${country}/journal/size-passport.html`
}

export const apiConfig = {
  getBaseUrl: getApiBaseUrl,
  getWebsiteBaseUrl,
  getNewArrivalsUrl,
  getStoresUrl,
  getPickupAppointmentUrl,
  getReturnsUrl,
  getSizePassportJournalUrl,
} as const
